import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faCog, faCoins } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function PuntiFedeltaConfig() {
    const [config, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`${API_URL}/api/punti-fedelta/configurazione`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConfig(response.data);
        } catch (err) {
            setError('Errore nel caricamento della configurazione');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            setError('');
            setSuccess('');

            const token = localStorage.getItem('access_token');
            await axios.put(`${API_URL}/api/punti-fedelta/configurazione`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSuccess('Configurazione salvata con successo!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Caricamento configurazione...</p>
            </div>
        );
    }

    if (!config) {
        return <Alert variant="danger">Impossibile caricare la configurazione</Alert>;
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center mb-4">
                <FontAwesomeIcon icon={faCog} size="2x" className="text-primary me-3" />
                <div>
                    <h1 className="mb-0">Configurazione Punti Fedeltà</h1>
                    <p className="text-muted mb-0">Gestisci i parametri del sistema punti fedeltà</p>
                </div>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            <Form onSubmit={handleSubmit}>
                <Row>
                    {/* Parametri Base */}
                    <Col md={6}>
                        <Card className="mb-4">
                            <Card.Header className="bg-primary text-white">
                                <FontAwesomeIcon icon={faCoins} className="me-2" />
                                Parametri Base
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Euro per punto</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={config.euro_per_punto}
                                        onChange={(e) => handleChange('euro_per_punto', parseFloat(e.target.value))}
                                    />
                                    <Form.Text className="text-muted">
                                        Es: 10 = 1 punto ogni €10 spesi
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Punti per euro sconto</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={config.punti_per_euro_sconto}
                                        onChange={(e) => handleChange('punti_per_euro_sconto', parseFloat(e.target.value))}
                                    />
                                    <Form.Text className="text-muted">
                                        Es: 20 = serve 20 punti per €1 di sconto
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Minimo punti per utilizzo</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.minimo_punti_utilizzo}
                                        onChange={(e) => handleChange('minimo_punti_utilizzo', parseInt(e.target.value))}
                                    />
                                    <Form.Text className="text-muted">
                                        Punti minimi richiesti per poterli usare
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Scadenza punti (mesi)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.punti_scadono_mesi || ''}
                                        onChange={(e) => handleChange('punti_scadono_mesi', e.target.value ? parseInt(e.target.value) : null)}
                                        placeholder="Lascia vuoto per nessuna scadenza"
                                    />
                                    <Form.Text className="text-muted">
                                        Lascia vuoto se i punti non scadono mai
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Check
                                        type="checkbox"
                                        label="Sistema attivo"
                                        checked={config.sistema_attivo}
                                        onChange={(e) => handleChange('sistema_attivo', e.target.checked)}
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Livelli Fedeltà */}
                    <Col md={6}>
                        <Card className="mb-4">
                            <Card.Header className="bg-warning text-dark">
                                <strong>Livelli Fedeltà</strong>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-4">
                                    <h6 className="text-muted">🥈 Silver</h6>
                                    <Row>
                                        <Col>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Soglia punti</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={config.soglia_silver}
                                                    onChange={(e) => handleChange('soglia_silver', parseInt(e.target.value))}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Moltiplicatore</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.1"
                                                    value={config.moltiplicatore_silver}
                                                    onChange={(e) => handleChange('moltiplicatore_silver', parseFloat(e.target.value))}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="mb-4">
                                    <h6 className="text-warning">🥇 Gold</h6>
                                    <Row>
                                        <Col>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Soglia punti</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={config.soglia_gold}
                                                    onChange={(e) => handleChange('soglia_gold', parseInt(e.target.value))}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Moltiplicatore</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.1"
                                                    value={config.moltiplicatore_gold}
                                                    onChange={(e) => handleChange('moltiplicatore_gold', parseFloat(e.target.value))}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Bonus Speciali */}
                        <Card className="mb-4">
                            <Card.Header className="bg-success text-white">
                                <strong>Bonus Speciali</strong>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>🎁 Primo acquisto</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.bonus_primo_acquisto}
                                        onChange={(e) => handleChange('bonus_primo_acquisto', parseInt(e.target.value))}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>🎂 Compleanno</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.bonus_compleanno}
                                        onChange={(e) => handleChange('bonus_compleanno', parseInt(e.target.value))}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>⭐ Recensione</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.bonus_recensione}
                                        onChange={(e) => handleChange('bonus_recensione', parseInt(e.target.value))}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>👥 Referral</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={config.bonus_referral}
                                        onChange={(e) => handleChange('bonus_referral', parseInt(e.target.value))}
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <div className="d-flex justify-content-end">
                    <Button variant="primary" type="submit" disabled={isSaving} size="lg">
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {isSaving ? 'Salvataggio...' : 'Salva Configurazione'}
                    </Button>
                </div>
            </Form>
        </div>
    );
}
